import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Button,
  Grid,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import AddSchoolDialog from './AddSchoolDialog';
import EditSchoolDialog from './EditSchoolDialog';
import { SchoolFormData } from '../../../../models';
import { stringToSemanticDisplay } from '../../../../utils/DateUtils';
import ConfirmRemoveDialog from '../nonNationalExam/ConfirmRemoveDialog';
import { IOptionType } from '../../../../api/candidate/exam/model';

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  addButtonContainer: {
    margin: theme.spacing(1),
    display: 'inline-flex',
    cursor: 'pointer',
    '&:hover': {
      '& $icon, & $button': {
        color: theme.color.link.linkHover01,
      },
      '& $iconRemove, $buttonRemove': {
        color: theme.color.link.linkHover02,
      },
    },
  },
  schoolInfoStatement: {
    marginBottom: theme.spacing(4),
  },
  button: {
    margin: 'auto',
    padding: 10,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
    paddingBottom: theme.spacing(3),
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  headerTitleContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const renderAdmissionDateColumn = (data: SchoolFormData[], dataIndex: number) => (
  <Typography variant="body2">
    {data[dataIndex] && data[dataIndex].dateOfAdmission
      ? stringToSemanticDisplay(data[dataIndex].dateOfAdmission.toString())
      : '-'}
  </Typography>
);

const renderCompletionDateColumn = (data: SchoolFormData[], dataIndex: number) => (
  <Typography variant="body2">
    {data[dataIndex] && data[dataIndex].dateOfCompletion
      ? stringToSemanticDisplay(data[dataIndex].dateOfCompletion.toString())
      : '-'}
  </Typography>
);

const renderCountryColumnAction = (data: SchoolFormData[], dataIndex: number) => {
  if (!data[dataIndex] || !data[dataIndex].country) {
    return '-';
  }
  const { country } = data[dataIndex];
  return (
    <Typography variant="body2">
      {typeof country === 'object' ? country.description : '-'}
    </Typography>
  );
};

const columnsNoAction = (t: TFunction, isAEIS: boolean, data: SchoolFormData[]) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: `${t('exam->>ReviewExam.School')}${isAEIS ? '' : '(s)'}`,
      name: 'schoolName',
      options: {
        setCellProps: () => ({ style: { width: '30%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.TypeOfSchool'),
      name: 'typeOfSchool',
    },
    {
      label: t('exam->>NonNationalExam.Country'),
      name: 'country',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderCountryColumnAction(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateOfAdmission'),
      name: 'dateOfAdmission',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderAdmissionDateColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateCompleting'),
      name: 'dateOfCompletion',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderCompletionDateColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.HighestLevelPassed'),
      name: 'highestLevelPassed',
    },
  ];

  return columns;
};

const columnsAction = (
  t: TFunction,
  isAEIS: boolean,
  data: SchoolFormData[],
  classes: Record<string,string>,
  theme: AugmentedTheme,
  editSchoolClick: (dataIndex: number) => () => void,
  deleteSchoolClick: (dataIndex: number) => () => void
) => {
  const columns: MUIDataTableColumnDef[] = [
    {
      label: `${t('exam->>ReviewExam.School')}${isAEIS ? '' : '(s)'}`,
      name: 'schoolName',
      options: {
        setCellProps: () => ({ style: { width: '20%' } }),
      },
    },
    {
      label: t('exam->>NonNationalExam.TypeOfSchool'),
      name: 'typeOfSchool',
    },
    {
      label: t('exam->>NonNationalExam.Country'),
      name: 'country',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderCountryColumnAction(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateOfAdmission'),
      name: 'dateOfAdmission',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderAdmissionDateColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.DateCompleting'),
      name: 'dateOfCompletion',
      options: {
        customBodyRenderLite: (dataIndex: number) => renderCompletionDateColumn(data, dataIndex),
      },
    },
    {
      label: t('exam->>NonNationalExam.HighestLevelPassed'),
      name: 'highestLevelPassed',
    },
    {
      name: 'action',
      label: t('exam->>NonNationalExam.Action'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Grid container spacing={2}>
              <Grid className={classes.buttonContainer}>
                <Icon size="M" name={IconName.edit} color={theme.color.icon.iconSelected} />
                <Button
                  id="button-accessArrangement-edit"
                  className={`${classes.button} ${classes.buttonFont}`}
                  onClick={editSchoolClick(dataIndex)}
                  variant="text"
                  color="primary"
                  size="small"
                >
                  {t('accessArrangement->>AccessArrangementRequest.Button.Edit')}
                </Button>
              </Grid>
              <Grid className={classes.buttonContainer}>
                <Icon size="M" name={IconName.trash} color={theme.color.icon.iconTrash} />
                <Button
                  id="button-accessArrangement-edit"
                  className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                  onClick={deleteSchoolClick(dataIndex)}
                  variant="text"
                  size="small"
                >
                  {t('exam->>ExamSchoolingDetails.Remove')}
                </Button>
              </Grid>
            </Grid>
          </>
        ),
      },
    },
  ];

  return columns;
};

interface SchoolInfoTableProps {
  isShowAction?: boolean;
  setSchools?: Dispatch<SetStateAction<SchoolFormData[]>>;
  data: SchoolFormData[];
  birthCountryData?: IOptionType[];
  isDisableAdd?: boolean;
  isAEIS?: boolean;
  isSPE?: boolean;
}

const SchoolInfoTable = ({
  isShowAction = true,
  data,
  setSchools,
  birthCountryData,
  isDisableAdd = false,
  isAEIS = true,
  isSPE = false,
}: SchoolInfoTableProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const [addDialogState, setAddDialogState] = useState<boolean>(false);
  const [editDialogState, setEditDialogState] = useState<boolean>(false);
  const [removeDialogState, setRemoveDialogState] = useState<boolean>(false);
  const [schoolSelected, setSchoolSelected] = useState<SchoolFormData>();
  const [indexSelected, setIndexSelected] = useState<number>(0);

  const addSchoolClick = () => {
    setAddDialogState(true);
  };

  const onAddDialogClose = () => {
    setAddDialogState(false);
  };

  const editSchoolClick = useCallback(
    (dataIndex: number) => () => {
      setIndexSelected(dataIndex);
      setSchoolSelected(data[dataIndex]);
      setEditDialogState(true);
    },
    [data]
  );

  const onEditDialogClose = () => {
    setEditDialogState(false);
  };

  const onUpdateDataTable = (updateValue: SchoolFormData) => {
    const newData = [...data];
    newData[indexSelected] = { ...updateValue };
    if (setSchools) {
      setSchools([...newData]);
    }
  };

  const deleteSchoolClick = useCallback(
    (dataIndex: number) => () => {
      setIndexSelected(dataIndex);
      setSchoolSelected(data[dataIndex]);
      setRemoveDialogState(true);
    },
    [data]
  );

  const onDeleteSchool = () => {
    const newData = [...data];
    newData.splice(indexSelected, 1);
    if (setSchools) {
      setSchools([...newData]);
    }
    setRemoveDialogState(false);
  };

  const columns: MUIDataTableColumnDef[] = useMemo(() => {
    if (!isShowAction) {
      return columnsNoAction(t, isAEIS, data);
    }
    return columnsAction(t, isAEIS, data, classes, theme, editSchoolClick, deleteSchoolClick);
  }, [classes, data, deleteSchoolClick, editSchoolClick, isAEIS, isShowAction, t, theme]);

  return (
    <Grid sm={12}>
      <Grid
        container
        direction="row"
        className={`${classes.headerContainer} ${isShowAction ? '' : classes.headerTitleContainer}`}
      >
        {!isSPE && isShowAction && (
          <Typography variant="h3">{t('exam->>NonNationalExam.SchoolInfo')}</Typography>
        )}
        {isSPE && isShowAction && (
          <Typography variant="h3">{t('exam->>NonNationalExam.SchoolingDetails')}</Typography>
        )}
        {isShowAction && (
          <Box className={classes.addButtonContainer}>
            <Icon
              size="M"
              className={classes.icon}
              name={IconName.add}
              color={
                isDisableAdd ? theme.color.link.linkDisabledBg01 : theme.color.icon.iconSelected
              }
            />
            <Button
              id="button-addSubject"
              className={classes.button}
              onClick={addSchoolClick}
              variant="text"
              color="primary"
              disabled={isDisableAdd}
            >
              {t('exam->>NonNationalExam.AddSchool')}
            </Button>
          </Box>
        )}
      </Grid>
      {isShowAction && (
        <Typography className={classes.schoolInfoStatement}>
          {isAEIS
            ? t('exam->>NonNationalExam.SchoolInfoAEISStatement')
            : t('exam->>NonNationalExam.SchoolInfoSPERSStatement')}
        </Typography>
      )}
      <MUIDataTable
        columns={columns}
        data={data}
        emptyDataLabel={t('exam->>NonNationalExam.SchoolEmpty')}
      />
      <AddSchoolDialog
        modalState={addDialogState}
        onDialogClose={onAddDialogClose}
        setSchools={setSchools}
        birthCountryData={birthCountryData}
        isAEIS={isAEIS}
      />
      <EditSchoolDialog
        modalState={editDialogState}
        onDialogClose={onEditDialogClose}
        schoolSelected={schoolSelected}
        birthCountryData={birthCountryData}
        isAEIS={isAEIS}
        onUpdateDataTable={onUpdateDataTable}
      />
      <ConfirmRemoveDialog
        contentText={t('exam->>ExamSchoolingDetails.RemoveSchoolContent')}
        modalState={removeDialogState}
        onDialogClose={() => setRemoveDialogState(false)}
        proceedClick={onDeleteSchool}
        title={`${t('exam->>ExamSchoolingDetails.Remove')} ${schoolSelected?.schoolName}${t(
          'exam->>ExamSchoolingDetails.FromSchoolInfo'
        )}`}
      />
    </Grid>
  );
};

export default SchoolInfoTable;
